import {
  bootstrapSigil,
  fetchTokenNumbers,
  getPromoCode,
  getRequestId,
  updateRequestPriority,
} from 'mapi-js'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { environment } from '../../environment-config'
import { appendBodyScript, appendHeaderScript } from '../utils/ga4Scripts'

const IS_LOCAL = environment === 'local'

const defaultMapiValues = {
  rotatedNumber: '1-555-555-5555',
  passPromoCode: () => {},
  passRingPool: () => {},
  defaultPromoCode: '',
  sessionPromoCode: '',
  siteURL: '',
  requestId: '',
  brandToken: '',
  brandTokens: [],
  analyticsId: '',
  chatEnabled: true,
}

export const MapiContext = createContext(defaultMapiValues)
export const useMapi = () => useContext(MapiContext)

/**
 * @param {string} number
 * @param {string} phoneSymbol
 */
function formatPhone(number, phoneSymbol) {
  if (!number) return ''
  const numberStr = String(number)
  return `1${phoneSymbol}${numberStr.substring(
    0,
    3
  )}${phoneSymbol}${numberStr.substring(
    3,
    6
  )}${phoneSymbol}${numberStr.substring(6, 10)}`
}

export function MapiProvider({ mapiConfig, children }) {
  const {
    siteURL,
    brandTokens,
    brandToken,
    defaultPhone,
    defaultPromoCode,
    defaultCprPriority,
    phoneSymbol = '-',
    analyticsId,
    chatEnabled,
  } = mapiConfig

  if (typeof defaultPhone !== 'string' && typeof defaultPhone !== 'number') {
    console.error(
      "%c Leshen:UI:Mapi Please provide a 'defaultPhone', in string form, in your site's gatsby-config.js to @leshen/gatsby-theme-leshen options.",
      'background: firebrick; color: white; border-radius: 3px; font-weight: bold;'
    )
  }

  // We don't want to set defaultPromoCode as initial state because Mapi would
  // be called with it on initial load, rather than the page promo if it exists
  const [requestId, setRequestId] = useState('')
  const [promoCode, setPromoCode] = useState()
  const [ringPool, passRingPool] = useState()
  const [cprPriority, setCprPriority] = useState()
  const [sessionPromoCode, setSessionPromoCode] = useState()
  const [rotatedNumber, setRotatedNumber] = useState(
    formatPhone(defaultPhone, phoneSymbol)
  )

  /**
   * Init Mapi
   * @params {string|null} [code] - if no code is passed the site default code is used
   * @params {string|null} [token] - if no token is passed the site brandToken is used
   */
  const initMapi = useCallback(
    (code, token, priority) => {
      setPromoCode(code || defaultPromoCode)
      passRingPool(token || brandToken)
      setCprPriority(priority || defaultCprPriority)
    },
    [setPromoCode, defaultPromoCode, defaultCprPriority, brandToken]
  )

  const initSessionPromoCode = useCallback(() => {
    getPromoCode()
      .then((code) => setSessionPromoCode(code))
      .catch((error) => {
        console.error('Leshen - failed to get Promo Code from MAPI:', error) // eslint-disable-line no-console
      })
  }, [setSessionPromoCode])

  // Init Mapi
  useEffect(() => {
    if (!promoCode || !ringPool || IS_LOCAL) return

    bootstrapSigil({
      track: true,
      defaultPhone,
      brand: ringPool,
      defaultPromoCode: promoCode,
      allowLastResortNumber: false,
      forceDomain: new URL(siteURL).hostname.replace('www.', ''),
      environment: environment === 'local' ? 'development' : environment,
    })
      .then(() => {
        appendHeaderScript(analyticsId)
        appendBodyScript(analyticsId)
        initSessionPromoCode()
      })
      .catch((err) => {
        console.error(err)
      })

    fetchTokenNumbers([ringPool])
      .then((data) => {
        const numberFromFetch = data?.tokens?.[ringPool]
        if (!numberFromFetch) {
          throw new Error('fetchTokenNumbers did not return the required data')
        }
        if (cprPriority) {
          updateRequestPriority(cprPriority)
        }
        setRotatedNumber(formatPhone(numberFromFetch, phoneSymbol))
      })
      .catch((error) => console.error('failed to fetch numbers:', error))
  }, [
    analyticsId,
    brandToken,
    promoCode,
    ringPool,
    phoneSymbol,
    defaultPhone,
    cprPriority,
    siteURL,
    initSessionPromoCode,
  ])

  // Get RequestId
  useEffect(() => {
    if (IS_LOCAL) {
      setRequestId('local-dev-rid')
      return
    }
    getRequestId()
      .then((id) => {
        setRequestId(id)
        if (typeof window.gtag === 'function') {
          window.gtag('set', 'user_properties', {
            request_id: id,
          })
        }
      })
      .catch((error) => {
        console.error('failed to fetch request ID:', error)
      })
  }, [])

  return (
    <MapiContext.Provider
      value={{
        defaultPromoCode,
        rotatedNumber,
        passPromoCode: initMapi, // TODO: remove once we know none of the sites are using it
        initMapi,
        passRingPool,
        sessionPromoCode,
        siteURL,
        requestId,
        brandToken,
        brandTokens,
        analyticsId,
        chatEnabled,
      }}
    >
      {children}
    </MapiContext.Provider>
  )
}
